import { useState } from "react";
import { Button, Card, Cluster, Input, Select, Stack, useToast } from "@salesapp/ui";
import { reportSaveBlocked, reportSaveFailure, reportSaved } from "../lib/saveNotice";
import {
  ACTIVITY_MEASUREMENTS,
  DEFAULT_ACTIVITY_TYPES,
  activityTypeValue,
  meaningForActivityType,
  sameActivityTypes,
  type ActivityType,
} from "../lib/activityTypes";
import { orgSettingsApi, type OrgAppSettings } from "../lib/orgSettingsApi";

type SettingsBody = Parameters<typeof orgSettingsApi.saveAppSettings>[1];

function storedTypes(settings: OrgAppSettings): ActivityType[] {
  return settings.activity_types?.length ? settings.activity_types : DEFAULT_ACTIVITY_TYPES;
}

export function ActivityTypesSettings({
  orgId,
  settings,
  settingsBody,
  saving,
  onSaved,
  onError,
  onNotice,
  onSaving,
}: {
  orgId: string;
  settings: OrgAppSettings;
  settingsBody: SettingsBody;
  saving: boolean;
  onSaved: (row: OrgAppSettings) => void;
  onError: (message: string | null) => void;
  onNotice: (message: string | null) => void;
  onSaving: (saving: boolean) => void;
}) {
  const { show } = useToast();
  const [rows, setRows] = useState<ActivityType[]>(() => storedTypes(settings));
  const [draftLabel, setDraftLabel] = useState("");
  const [draftMeasurement, setDraftMeasurement] = useState("activity_call");
  const [localError, setLocalError] = useState<string | null>(null);
  const loadedKey = JSON.stringify(
    storedTypes(settings).map(({ value, label, measurement }) => ({ value, label, measurement })),
  );
  const [seenKey, setSeenKey] = useState(loadedKey);
  if (seenKey !== loadedKey) {
    setSeenKey(loadedKey);
    setRows(storedTypes(settings));
  }

  const dirty = !sameActivityTypes(rows, storedTypes(settings));

  function addType() {
    const label = draftLabel.trim();
    if (!label) {
      setLocalError(reportSaveBlocked(show, "Enter a name for the activity type."));
      return;
    }
    if (label.length > 40) {
      setLocalError(reportSaveBlocked(show, "Keep the name to 40 characters."));
      return;
    }
    if (rows.some((item) => item.label.toLowerCase() === label.toLowerCase())) {
      setLocalError(reportSaveBlocked(show, "That name is already in the list."));
      return;
    }
    if (rows.length >= 20) {
      setLocalError(reportSaveBlocked(show, "Keep the list to 20 activity types."));
      return;
    }
    const value = activityTypeValue(label, rows.map((item) => item.value));
    if (!value) {
      setLocalError(reportSaveBlocked(show, "Use a name that starts with a letter."));
      return;
    }
    setRows([
      ...rows,
      {
        value,
        label,
        measurement: draftMeasurement || null,
        meaning: meaningForActivityType({ measurement: draftMeasurement || null }),
      },
    ]);
    setDraftLabel("");
    setLocalError(null);
  }

  function save(next: ActivityType[]) {
    setLocalError(null);
    onError(null);
    onNotice(null);
    onSaving(true);
    const activity_types = next.map(({ value, label, measurement }) => ({ value, label, measurement }));
    return orgSettingsApi.saveAppSettings(orgId, { ...settingsBody, activity_types }).then((row) => {
      onSaved(row);
      onNotice("Activity types saved.");
      reportSaved(show);
    }).catch((err) => {
      const message = reportSaveFailure(show, err, "Activity types could not be saved.");
      setLocalError(message);
      onError(message);
    }).finally(() => onSaving(false));
  }

  return (
    <Card>
      <Stack gap={4}>
        <h2 className="home-section-title">Log activity types</h2>
        <p className="home-subtitle">
          These are the choices on Log activity. Point each one at the sales-plan KPI it should count toward:
          cold calls, emails, meetings, or demos. Does not count stores the activity and leaves it off the plan.
          Older activities saved as Call still count as cold calls. Mail sent from the mailbox is stored as Email,
          so leave Email counting as Emails if that mail should count.
        </p>
        {localError ? <p role="alert" className="settings-error">{localError}</p> : null}
        <Stack gap={3}>
          {rows.map((row) => (
            <Stack key={row.value} gap={2}>
              <Input
                label={`Name for ${row.label}`}
                value={row.label}
                maxLength={40}
                onChange={(event) => {
                  const label = event.target.value;
                  setRows((current) => current.map((item) => (item.value === row.value ? { ...item, label } : item)));
                }}
              />
              <Select
                label={`Counts as for ${row.label}`}
                tooltip={meaningForActivityType(row)}
                value={row.measurement ?? ""}
                onChange={(event) => {
                  const measurement = event.target.value || null;
                  setRows((current) => current.map((item) => (
                    item.value === row.value
                      ? { ...item, measurement, meaning: meaningForActivityType({ measurement }) }
                      : item
                  )));
                }}
              >
                {ACTIVITY_MEASUREMENTS.map((item) => (
                  <option key={item.value || "none"} value={item.value}>{item.label}</option>
                ))}
              </Select>
              <Cluster gap={2}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={rows.length <= 1 || saving}
                  onClick={() => setRows((current) => current.filter((item) => item.value !== row.value))}
                >
                  {`Remove ${row.label}`}
                </Button>
              </Cluster>
            </Stack>
          ))}
        </Stack>
        <Stack gap={2}>
          <Input
            label="New activity type"
            tooltip="A name your team will recognise on Log activity, such as Site visit or LinkedIn message."
            value={draftLabel}
            maxLength={40}
            onChange={(event) => setDraftLabel(event.target.value)}
          />
          <Select
            label="Counts on the sales plan"
            tooltip="Cold calls, emails, meetings, and demos are the activity KPIs on a sales plan. Does not count leaves the activity off those totals."
            value={draftMeasurement}
            onChange={(event) => setDraftMeasurement(event.target.value)}
          >
            {ACTIVITY_MEASUREMENTS.map((item) => (
              <option key={item.value || "none"} value={item.value}>{item.label}</option>
            ))}
          </Select>
          <Cluster gap={2}>
            <Button type="button" variant="secondary" disabled={saving} onClick={addType}>
              Add activity type
            </Button>
          </Cluster>
        </Stack>
        <Cluster gap={2}>
          <Button type="button" disabled={!dirty || saving} onClick={() => { void save(rows); }}>
            {saving ? "Saving…" : "Save activity types"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={saving || sameActivityTypes(rows, DEFAULT_ACTIVITY_TYPES)}
            onClick={() => {
              setLocalError(null);
              if (sameActivityTypes(storedTypes(settings), DEFAULT_ACTIVITY_TYPES)) {
                setRows(DEFAULT_ACTIVITY_TYPES);
                return;
              }
              void save(DEFAULT_ACTIVITY_TYPES);
            }}
          >
            Restore defaults
          </Button>
        </Cluster>
      </Stack>
    </Card>
  );
}
